import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  BottomSheet,
  Input,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useRef } from "react";

import { IconButton } from "../../components/IconButton";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { WorkplacesList } from "./List";
import { useWorkplaceList } from "./useWorkplaceList";

interface WorkplacesListBottomSheetProps {
  modalState: UseModalState;
}

export function WorkplacesListBottomSheet(props: WorkplacesListBottomSheetProps) {
  const { modalState } = props;
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    isLoadingWorkplaces,
    isMatchingWorkplacesError,
    isMatchingWorkplacesSuccess,
    maxDistanceFetched,
    matchingWorkplacesGroup,
    allOtherWorkplacesGroup,
    isFetchingNextPage,
    hasNextPage,
    handleLoadMore,
  } = useWorkplaceList();

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        height: `calc(100% - ${getSafeAreaPaddings({ top: true }).paddingTop})`,
      }}
    >
      <DialogContent
        sx={{
          gap: 5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack sx={{ position: "relative", pt: 3 }}>
          <Stack spacing={3}>
            <Text semibold variant="h5" align="center">
              Find a workplace
            </Text>
            <Text
              semibold
              align="center"
              variant="caption"
              color={(theme) => theme.palette.text.secondary}
              sx={{ opacity: isLoadingWorkplaces ? 0 : 1 }}
            >
              all locations within {maxDistanceFetched} mi of home
            </Text>
          </Stack>
          <IconButton
            size="small"
            variant="outlined"
            iconType="close"
            sx={(theme) => ({
              boxShadow: theme.shadows[2],
              outlineColor: theme.palette.border?.subtle,
              position: "absolute",
              right: 0,
              top: 0,
            })}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.BROWSE_WORKPLACES_CLOSED);
              modalState.closeModal();
            }}
          />
        </Stack>

        <Input
          placeholder="Search for a workplace"
          iconType="search"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
          }}
        />

        <Stack
          sx={(theme) => ({
            height: "100%",
            minHeight: theme.size?.bottomSheet.loading.minHeight,
            justifyContent: isLoadingWorkplaces ? "center" : "flex-start",
          })}
        >
          <WorkplacesList
            isLoading={isLoadingWorkplaces}
            isEmpty={
              matchingWorkplacesGroup.totalCount === 0 && allOtherWorkplacesGroup.totalCount === 0
            }
            isError={isMatchingWorkplacesError}
            isSuccess={isMatchingWorkplacesSuccess}
            scrollRef={scrollRef}
            searchTerm={debouncedSearchTerm}
            distanceFilter={maxDistanceFetched}
            workplacesMatchingFiltersGroup={matchingWorkplacesGroup}
            allOtherWorkplacesGroup={allOtherWorkplacesGroup}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={hasNextPage ? handleLoadMore : undefined}
          />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
